import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Plus, X, Save, ArrowLeft, Check, AlertCircle, Link2, Upload, Loader2, ImageIcon } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useTenant } from '@/contexts/TenantContext';

export default function ProductConfigForm() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { tenantPath } = useTenant();
  const [loading, setLoading] = useState(false);
  const [optionLists, setOptionLists] = useState({});
  const [allOptionLists, setAllOptionLists] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = React.useRef(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image is too large. Max size is 5MB.');
      e.target.value = '';
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    setUploadingImage(true);
    try {
      const response = await api.post('/admin/upload-product-image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFormData((prev) => ({ ...prev, image_url: response.data.url }));
      toast.success('Image uploaded');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Upload failed';
      toast.error(detail);
    } finally {
      setUploadingImage(false);
      if (e.target) e.target.value = '';
    }
  };
  
  const [formData, setFormData] = useState({
    name: '',
    manufacturer: '',
    category: 'shafts',
    cost_price: null,
    min_advertised_price: null,
    stock: 0,
    description: '',
    image_url: '',
    shaft_characteristics: {
      material: '',
      torque: '',
      kick_point: '',
      bend_profile: '',
      launch: '',
      spin: '',
      balance_point: ''
    },
    shaft_flex_options: [],
    adapter_type_options: [],
    playing_length_options: [],
    tipping_options: [],
    grip_options: [],
    grip_wraps_options: [],
    grip_logo_options: [],
    pured_options: [],
    variant_options: [],
    option_price_overrides: {},
    required_options: [],
    linked_option_lists: []
  });

  // Shaft characteristics dropdown options
  const shaftCharacteristicOptions = {
    material: ['Graphite', 'Steel'],
    torque: ['Low', 'Mid', 'High'],
    kick_point: ['Low', 'Mid', 'High'],
    launch: ['Low', 'Mid', 'High'],
    spin: ['Low', 'Mid', 'High']
  };

  const updateShaftCharacteristic = (field, value) => {
    setFormData(prev => ({
      ...prev,
      shaft_characteristics: {
        ...prev.shaft_characteristics,
        [field]: value
      }
    }));
  };

  // Option field definitions for shafts
  const shaftOptionFields = [
    { key: 'shaft_flex', label: 'Shaft Flex' },
    { key: 'adapter_type', label: 'Club Type / Adapter' },
    { key: 'playing_length', label: 'Playing Length' },
    { key: 'tipping', label: 'Tipping' },
    { key: 'grip', label: 'Grip' },
    { key: 'grip_wraps', label: 'Grip Wraps' },
    { key: 'grip_logo', label: 'Grip Logo' },
    { key: 'pured', label: 'Pured' }
  ];

  const toggleRequiredOption = (optionKey) => {
    setFormData(prev => {
      const currentRequired = prev.required_options || [];
      if (currentRequired.includes(optionKey)) {
        return { ...prev, required_options: currentRequired.filter(k => k !== optionKey) };
      } else {
        return { ...prev, required_options: [...currentRequired, optionKey] };
      }
    });
  };

  const isOptionRequired = (optionKey) => {
    return (formData.required_options || []).includes(optionKey);
  };

  const toggleLinkedOptionList = (listId) => {
    setFormData(prev => {
      const current = prev.linked_option_lists || [];
      if (current.includes(listId)) {
        return { ...prev, linked_option_lists: current.filter(id => id !== listId) };
      } else {
        return { ...prev, linked_option_lists: [...current, listId] };
      }
    });
  };

  const isOptionListLinked = (listId) => {
    return (formData.linked_option_lists || []).includes(listId);
  };

  useEffect(() => {
    loadOptionLists();
  }, []);

  useEffect(() => {
    if (productId && productId !== 'new' && !loadingOptions) {
      loadProduct();
    }
  }, [productId, loadingOptions]);

  const loadOptionLists = async () => {
    try {
      const response = await api.get('/admin/option-lists');
      setAllOptionLists(response.data);
      const listsMap = {};
      response.data.forEach(list => {
        listsMap[list.category] = list.options;
      });
      setOptionLists(listsMap);
    } catch (error) {
      toast.error('Failed to load option lists');
    } finally {
      setLoadingOptions(false);
    }
  };

  const loadProduct = async () => {
    try {
      const response = await api.get(`/products/${productId}`);
      setFormData(response.data);
    } catch (error) {
      toast.error('Failed to load product');
      navigate(tenantPath('/admin/products'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (productId && productId !== 'new') {
        await api.put(`/admin/products/${productId}`, formData);
        toast.success('Product updated successfully!');
      } else {
        await api.post('/admin/products', formData);
        toast.success('Product created successfully!');
      }
      navigate(tenantPath('/admin/products'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  // Toggle option selection from master list
  const toggleOption = (field, option) => {
    setFormData(prev => {
      const currentOptions = prev[field] || [];
      const exists = currentOptions.find(opt => opt.id === option.id);
      
      if (exists) {
        return {
          ...prev,
          [field]: currentOptions.filter(opt => opt.id !== option.id)
        };
      } else {
        return {
          ...prev,
          [field]: [...currentOptions, { id: option.id, name: option.name }]
        };
      }
    });
  };

  const isOptionSelected = (field, optionId) => {
    return (formData[field] || []).some(opt => opt.id === optionId);
  };

  // Adapter Type with nested tip adapters
  const toggleAdapterType = (adapterName) => {
    setFormData(prev => {
      const currentAdapters = prev.adapter_type_options || [];
      const exists = currentAdapters.find(a => a.name === adapterName);
      
      if (exists) {
        return {
          ...prev,
          adapter_type_options: currentAdapters.filter(a => a.name !== adapterName)
        };
      } else {
        return {
          ...prev,
          adapter_type_options: [...currentAdapters, { 
            id: adapterName.toLowerCase(), 
            name: adapterName, 
            tip_adapters: [] 
          }]
        };
      }
    });
  };

  const isAdapterTypeSelected = (adapterName) => {
    return (formData.adapter_type_options || []).some(a => a.name === adapterName);
  };

  const toggleTipAdapter = (adapterName, tipOption) => {
    setFormData(prev => {
      const currentAdapters = prev.adapter_type_options || [];
      return {
        ...prev,
        adapter_type_options: currentAdapters.map(adapter => {
          if (adapter.name !== adapterName) return adapter;
          
          const currentTips = adapter.tip_adapters || [];
          const exists = currentTips.find(t => t.id === tipOption.id);
          
          if (exists) {
            return {
              ...adapter,
              tip_adapters: currentTips.filter(t => t.id !== tipOption.id)
            };
          } else {
            return {
              ...adapter,
              tip_adapters: [...currentTips, { id: tipOption.id, name: tipOption.name }]
            };
          }
        })
      };
    });
  };

  const isTipAdapterSelected = (adapterName, tipId) => {
    const adapter = (formData.adapter_type_options || []).find(a => a.name === adapterName);
    return adapter?.tip_adapters?.some(t => t.id === tipId) || false;
  };

  // Get available club types from option lists (tip_adapters_*)
  const getClubTypes = () => {
    return Object.keys(optionLists)
      .filter(key => key.startsWith('tip_adapters_'))
      .map(key => ({
        key,
        name: key.replace('tip_adapters_', '').replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
      }));
  };

  if (loadingOptions) {
    return (
      <div className="text-center py-12">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#334155] border-r-transparent"></div>
        <p className="mt-4 text-gray-600">Loading options...</p>
      </div>
    );
  }

  const clubTypes = getClubTypes();

  return (
    <div className="max-w-5xl mx-auto" data-testid="product-config-form">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
          {productId && productId !== 'new' ? 'Edit Product' : 'Add New Product'}
        </h2>
        <button
          onClick={() => navigate(tenantPath('/admin/products'))}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>Back to Products</span>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Basic Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label>Product Name *</Label>
              <Input
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Fujikura 2024 Ventus Blue"
                data-testid="product-name-input"
              />
            </div>

            <div>
              <Label>Manufacturer *</Label>
              <Input
                required
                value={formData.manufacturer}
                onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                placeholder="Fujikura"
                data-testid="product-manufacturer-input"
              />
            </div>

            <div>
              <Label>Category *</Label>
              <select
                required
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full h-10 px-3 bg-white border border-gray-200 rounded-lg"
                data-testid="product-category-select"
              >
                <option value="shafts">Custom Shafts</option>
                <option value="drivers">Custom Drivers</option>
                <option value="fairways">Custom Fairways</option>
                <option value="hybrids">Custom Hybrids</option>
                <option value="irons">Custom Irons</option>
                <option value="wedges">Custom Wedges</option>
                <option value="putters">Custom Putters</option>
                <option value="grips">Grips</option>
                <option value="pre-owned">Pre-Owned</option>
              </select>
            </div>

            <div>
              <Label>MAP — Customer-Facing Price (USD) *</Label>
              <Input
                required
                type="number"
                step="0.01"
                value={formData.min_advertised_price ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setFormData({ ...formData, min_advertised_price: v === '' ? null : parseFloat(v) });
                }}
                placeholder="350.00"
                data-testid="product-map-input"
              />
              <p className="text-xs text-gray-500 mt-1">This is what customers see on every storefront page.</p>
            </div>

            <div>
              <Label>Wholesale Price (USD)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.cost_price ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setFormData({ ...formData, cost_price: v === '' ? null : parseFloat(v) });
                }}
                placeholder="Internal — not shown to customers"
                data-testid="product-wholesale-price-input"
              />
              <p className="text-xs text-gray-500 mt-1">Cost paid per unit. Internal only — never displayed to customers.</p>
            </div>

            <div>
              <Label>Stock Quantity *</Label>
              <Input
                required
                type="number"
                value={formData.stock}
                onChange={(e) => setFormData({ ...formData, stock: parseInt(e.target.value) })}
                placeholder="15"
                data-testid="product-stock-input"
              />
            </div>

            <div>
              <Label>Product Image</Label>
              <div className="space-y-2">
                {formData.image_url ? (
                  <div className="flex items-center gap-3 p-2 border border-gray-200 rounded-md bg-gray-50">
                    <img
                      src={formData.image_url}
                      alt="Product preview"
                      className="h-14 w-14 object-cover rounded border border-gray-200"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      data-testid="product-image-preview"
                    />
                    <div className="flex-1 truncate text-xs text-gray-600" title={formData.image_url}>
                      {formData.image_url}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setFormData({ ...formData, image_url: '' })}
                      data-testid="product-image-remove"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-xs text-gray-500 p-3 border border-dashed border-gray-300 rounded-md">
                    <ImageIcon className="h-4 w-4" />
                    No image attached
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    className="hidden"
                    onChange={handleImageUpload}
                    data-testid="product-image-file-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    data-testid="product-image-upload-btn"
                  >
                    {uploadingImage ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    ) : (
                      <Upload className="h-4 w-4 mr-1" />
                    )}
                    {uploadingImage ? 'Uploading...' : 'Upload image'}
                  </Button>
                  <span className="text-xs text-gray-400 self-center">or paste URL:</span>
                </div>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="https://..."
                  data-testid="product-image-input"
                />
                <p className="text-xs text-gray-500">JPG, PNG, GIF, or WEBP. Max 5MB.</p>
              </div>
            </div>

            <div className="md:col-span-2">
              <Label>Description *</Label>
              <Textarea
                required
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Product description..."
                rows={4}
                data-testid="product-description-input"
              />
            </div>
          </div>
        </div>

        {/* Shaft Characteristics - Only for shaft products */}
        {formData.category === 'shafts' && (
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Shaft Characteristics</h3>
            <p className="text-gray-600 text-sm mb-6">Define the technical specifications for this shaft</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Material */}
              <div>
                <Label>Material</Label>
                <select
                  value={formData.shaft_characteristics?.material || ''}
                  onChange={(e) => updateShaftCharacteristic('material', e.target.value)}
                  className="w-full h-10 px-3 bg-white border border-gray-200 rounded-lg"
                  data-testid="shaft-material-select"
                >
                  <option value="">Select Material</option>
                  {shaftCharacteristicOptions.material.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Graphite is lighter than Steel</p>
              </div>

              {/* Torque */}
              <div>
                <Label>Torque</Label>
                <select
                  value={formData.shaft_characteristics?.torque || ''}
                  onChange={(e) => updateShaftCharacteristic('torque', e.target.value)}
                  className="w-full h-10 px-3 bg-white border border-gray-200 rounded-lg"
                  data-testid="shaft-torque-select"
                >
                  <option value="">Select Torque</option>
                  {shaftCharacteristicOptions.torque.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Twist resistance (°)</p>
              </div>

              {/* Kick Point */}
              <div>
                <Label>Kick Point</Label>
                <select
                  value={formData.shaft_characteristics?.kick_point || ''}
                  onChange={(e) => updateShaftCharacteristic('kick_point', e.target.value)}
                  className="w-full h-10 px-3 bg-white border border-gray-200 rounded-lg"
                  data-testid="shaft-kickpoint-select"
                >
                  <option value="">Select Kick Point</option>
                  {shaftCharacteristicOptions.kick_point.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Bend location on shaft</p>
              </div>

              {/* Bend Profile */}
              <div>
                <Label>Bend Profile</Label>
                <Input
                  value={formData.shaft_characteristics?.bend_profile || ''}
                  onChange={(e) => updateShaftCharacteristic('bend_profile', e.target.value)}
                  placeholder="e.g., Stiffer tip"
                  data-testid="shaft-bendprofile-input"
                />
                <p className="text-xs text-gray-500 mt-1">How the shaft bends</p>
              </div>

              {/* Launch */}
              <div>
                <Label>Launch Category</Label>
                <select
                  value={formData.shaft_characteristics?.launch || ''}
                  onChange={(e) => updateShaftCharacteristic('launch', e.target.value)}
                  className="w-full h-10 px-3 bg-white border border-gray-200 rounded-lg"
                  data-testid="shaft-launch-select"
                >
                  <option value="">Select Launch</option>
                  {shaftCharacteristicOptions.launch.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Typical launch angle</p>
              </div>

              {/* Spin */}
              <div>
                <Label>Spin</Label>
                <select
                  value={formData.shaft_characteristics?.spin || ''}
                  onChange={(e) => updateShaftCharacteristic('spin', e.target.value)}
                  className="w-full h-10 px-3 bg-white border border-gray-200 rounded-lg"
                  data-testid="shaft-spin-select"
                >
                  <option value="">Select Spin</option>
                  {shaftCharacteristicOptions.spin.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Relative spin rate</p>
              </div>

              {/* Balance Point */}
              <div>
                <Label>Balance Point</Label>
                <Input
                  value={formData.shaft_characteristics?.balance_point || ''}
                  onChange={(e) => updateShaftCharacteristic('balance_point', e.target.value)}
                  placeholder="e.g., 52%"
                  data-testid="shaft-balancepoint-input"
                />
                <p className="text-xs text-gray-500 mt-1">% from tip (affects swingweight)</p>
              </div>
            </div>
          </div>
        )}

        {/* Shaft Configuration Options - Show for shafts and club categories */}
        {['shafts', 'drivers', 'fairways', 'hybrids', 'irons', 'wedges', 'putters'].includes(formData.category) && (
          <>
            {/* Required Options Configuration */}
            <div className="bg-white p-6 rounded-lg border border-[#E8E4DC]">
              <div className="flex items-center space-x-2 mb-2">
                <AlertCircle className="h-5 w-5 text-[#D4A55A]" />
                <h3 className="text-xl font-bold text-[#2C3E2D]">Required Options (Custom Build)</h3>
              </div>
              <p className="text-[#5A6B5C] text-sm mb-4">
                Select which options customers must choose when doing a custom build
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {shaftOptionFields.map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggleRequiredOption(key)}
                    className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                      isOptionRequired(key)
                        ? 'bg-[#D4A55A]/10 border-[#D4A55A] text-[#2C3E2D]'
                        : 'bg-[#F5F3EF] border-[#E8E4DC] text-[#5A6B5C] hover:border-[#D4A55A]'
                    }`}
                    data-testid={`required-${key}-toggle`}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      {isOptionRequired(key) && <Check className="h-4 w-4 text-[#D4A55A]" />}
                      <span>{label}</span>
                    </div>
                    {isOptionRequired(key) && (
                      <span className="text-xs text-[#D4A55A] mt-1 block">Required</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Linked Option Lists */}
            <div className="bg-white p-6 rounded-lg border border-[#E8E4DC]">
              <div className="flex items-center space-x-2 mb-2">
                <Link2 className="h-5 w-5 text-[#4A6741]" />
                <h3 className="text-xl font-bold text-[#2C3E2D]">Linked Option Lists</h3>
              </div>
              <p className="text-[#5A6B5C] text-sm mb-4">
                Select which option lists are available for this product. Only linked lists will appear as configuration options.
              </p>
              
              {allOptionLists.length === 0 ? (
                <p className="text-[#5A6B5C] text-center py-4">No option lists available. Create them in the Option Lists section first.</p>
              ) : (
                <div className="space-y-2" data-testid="linked-option-lists-section">
                  {allOptionLists.map(list => (
                    <button
                      key={list.list_id}
                      type="button"
                      onClick={() => toggleLinkedOptionList(list.list_id)}
                      className={`w-full flex items-center justify-between p-3 rounded-lg border-2 text-sm transition-all text-left ${
                        isOptionListLinked(list.list_id)
                          ? 'bg-[#4A6741]/10 border-[#4A6741] text-[#2C3E2D]'
                          : 'bg-[#F5F3EF] border-[#E8E4DC] text-[#5A6B5C] hover:border-[#4A6741]/50'
                      }`}
                      data-testid={`link-option-list-${list.list_id}`}
                    >
                      <div>
                        <span className="font-medium">{list.name}</span>
                        <span className="ml-2 text-xs opacity-70">({list.category})</span>
                        <span className="ml-2 text-xs opacity-50">{list.options?.length || 0} options</span>
                      </div>
                      {isOptionListLinked(list.list_id) && <Check className="h-4 w-4 text-[#4A6741] flex-shrink-0" />}
                    </button>
                  ))}
                  {(formData.linked_option_lists || []).length > 0 && (
                    <p className="text-xs text-[#5A6B5C] pt-2">
                      {formData.linked_option_lists.length} option list{formData.linked_option_lists.length !== 1 ? 's' : ''} linked
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Shaft Flex Options */}
            <OptionSelector
              title="Shaft Flex Options"
              description="Select available shaft flex options for this product"
              options={optionLists.shaft_flex || []}
              selectedOptions={formData.shaft_flex_options}
              onToggle={(opt) => toggleOption('shaft_flex_options', opt)}
              isSelected={(optId) => isOptionSelected('shaft_flex_options', optId)}
              emptyMessage="No shaft flex options available. Create them in Option Lists."
              testIdPrefix="shaft-flex"
              isRequired={isOptionRequired('shaft_flex')}
            />

            {/* Club Types & Tip Adapters */}
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Club Types & Tip Adapters</h3>
              <p className="text-gray-600 text-sm mb-4">Select club types and their compatible tip adapters</p>
              
              {clubTypes.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No club types available. Create tip adapter options in Option Lists.</p>
              ) : (
                <div className="space-y-4">
                  {clubTypes.map(({ key, name }) => (
                    <div key={key} className="border border-gray-200 rounded-lg overflow-hidden">
                      <div 
                        className={`flex items-center justify-between p-3 cursor-pointer ${
                          isAdapterTypeSelected(name) ? 'bg-[#334155] text-white' : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                        onClick={() => toggleAdapterType(name)}
                        data-testid={`club-type-${key}`}
                      >
                        <span className="font-medium">{name}</span>
                        {isAdapterTypeSelected(name) && <Check className="h-5 w-5" />}
                      </div>
                      
                      {isAdapterTypeSelected(name) && (
                        <div className="p-3 bg-white border-t border-gray-200">
                          <p className="text-sm text-gray-600 mb-2">Select tip adapters:</p>
                          <div className="flex flex-wrap gap-2">
                            {(optionLists[key] || []).map(tip => (
                              <button
                                key={tip.id}
                                type="button"
                                onClick={() => toggleTipAdapter(name, tip)}
                                className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                                  isTipAdapterSelected(name, tip.id)
                                    ? 'bg-[#334155] text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                                data-testid={`tip-adapter-${tip.id}`}
                              >
                                {tip.name}
                                {isTipAdapterSelected(name, tip.id) && (
                                  <Check className="h-3 w-3 ml-1 inline" />
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Playing Length Options */}
            <OptionSelector
              title="Playing Length Options"
              description="Select available playing length options"
              options={optionLists.playing_length || []}
              selectedOptions={formData.playing_length_options}
              onToggle={(opt) => toggleOption('playing_length_options', opt)}
              isSelected={(optId) => isOptionSelected('playing_length_options', optId)}
              emptyMessage="No playing length options available. Create them in Option Lists."
              testIdPrefix="playing-length"
              isRequired={isOptionRequired('playing_length')}
            />

            {/* Tipping Options */}
            <OptionSelector
              title="Tipping Options"
              description="Select available tipping options"
              options={optionLists.tipping || []}
              selectedOptions={formData.tipping_options}
              onToggle={(opt) => toggleOption('tipping_options', opt)}
              isSelected={(optId) => isOptionSelected('tipping_options', optId)}
              emptyMessage="No tipping options available. Create them in Option Lists."
              testIdPrefix="tipping"
              isRequired={isOptionRequired('tipping')}
            />

            {/* Grip Options */}
            <OptionSelector
              title="Grip Options"
              description="Select available grip options"
              options={optionLists.grips || []}
              selectedOptions={formData.grip_options}
              onToggle={(opt) => toggleOption('grip_options', opt)}
              isSelected={(optId) => isOptionSelected('grip_options', optId)}
              emptyMessage="No grip options available. Create them in Option Lists."
              testIdPrefix="grip"
              isRequired={isOptionRequired('grip')}
            />

            {/* Grip Wraps Options */}
            <OptionSelector
              title="Grip Wraps Options"
              description="Select available grip wrap options"
              options={optionLists.grip_wraps || []}
              selectedOptions={formData.grip_wraps_options}
              onToggle={(opt) => toggleOption('grip_wraps_options', opt)}
              isSelected={(optId) => isOptionSelected('grip_wraps_options', optId)}
              emptyMessage="No grip wrap options available. Create them in Option Lists."
              testIdPrefix="grip-wraps"
              isRequired={isOptionRequired('grip_wraps')}
            />

            {/* Grip Logo Options */}
            <OptionSelector
              title="Grip Logo Options"
              description="Select available grip logo options"
              options={optionLists.grip_logo || []}
              selectedOptions={formData.grip_logo_options}
              onToggle={(opt) => toggleOption('grip_logo_options', opt)}
              isSelected={(optId) => isOptionSelected('grip_logo_options', optId)}
              emptyMessage="No grip logo options available. Create them in Option Lists."
              testIdPrefix="grip-logo"
              isRequired={isOptionRequired('grip_logo')}
            />

            {/* Pured Options */}
            <OptionSelector
              title="Pured Options"
              description="Select available pured options"
              options={optionLists.pured || []}
              selectedOptions={formData.pured_options}
              onToggle={(opt) => toggleOption('pured_options', opt)}
              isSelected={(optId) => isOptionSelected('pured_options', optId)}
              emptyMessage="No pured options available. Create them in Option Lists."
              testIdPrefix="pured"
              isRequired={isOptionRequired('pured')}
            />
          </>
        )}

        {/* Placeholder for other categories that don't have shaft options */}
        {!['shafts', 'drivers', 'fairways', 'hybrids', 'irons', 'wedges', 'putters'].includes(formData.category) && (
          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 text-center">
            <p className="text-gray-600">
              Product options for <span className="font-semibold">{formData.category}</span> category are minimal.
            </p>
            <p className="text-gray-500 text-sm mt-2">
              Basic product information is sufficient for this category.
            </p>
          </div>
        )}

        {/* Price Overrides — let admin waive or override upcharges for specific options on this product */}
        {['shafts', 'drivers', 'fairways', 'hybrids', 'irons', 'wedges', 'putters'].includes(formData.category) && (
          <PriceOverridesSection
            formData={formData}
            setFormData={setFormData}
          />
        )}

        {/* Catalog Shaft Overrides — waive upcharge for specific shafts from the master shaft catalog */}
        {['drivers', 'fairways', 'hybrids', 'irons', 'wedges'].includes(formData.category) && (
          <CatalogShaftOverridesSection
            formData={formData}
            setFormData={setFormData}
          />
        )}

        {/* Submit Buttons */}
        <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate(tenantPath('/admin/products'))}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex items-center space-x-2 px-6 py-3 bg-slate-700 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50"
            data-testid="save-product-btn"
          >
            <Save className="h-5 w-5" />
            <span>{loading ? 'Saving...' : 'Save Product'}</span>
          </button>
        </div>
      </form>
    </div>
  );
}

// Reusable Option Selector Component
function OptionSelector({ title, description, options, selectedOptions, onToggle, isSelected, emptyMessage, testIdPrefix, isRequired }) {
  return (
    <div className={`bg-white p-6 rounded-lg border ${isRequired ? 'border-[#D4A55A]' : 'border-[#E8E4DC]'}`}>
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-xl font-bold text-[#2C3E2D]">{title}</h3>
        {isRequired && (
          <span className="text-xs font-bold text-[#D4A55A] bg-[#D4A55A]/10 px-2 py-1 rounded">
            REQUIRED
          </span>
        )}
      </div>
      <p className="text-[#5A6B5C] text-sm mb-4">{description}</p>
      
      {options.length === 0 ? (
        <p className="text-[#5A6B5C] text-center py-4">{emptyMessage}</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {options.map(opt => (
            <button
              key={opt.id}
              type="button"
              onClick={() => onToggle(opt)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                isSelected(opt.id)
                  ? 'bg-[#4A6741] text-white'
                  : 'bg-[#F5F3EF] text-[#5A6B5C] hover:bg-[#E8E4DC]'
              }`}
              data-testid={`${testIdPrefix}-option-${opt.id}`}
            >
              {opt.name}
              {isSelected(opt.id) && <Check className="h-4 w-4 ml-2 inline" />}
            </button>
          ))}
        </div>
      )}
      
      {selectedOptions && selectedOptions.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            Selected: <span className="font-medium text-gray-900">{selectedOptions.length}</span> options
          </p>
        </div>
      )}
    </div>
  );
}



// Maps a product-level option category to its formData array + a human label.
// Keys must match the keys used in configuration objects on the PDP/cart + the
// option_price_overrides DB field.
const OVERRIDE_CATEGORIES = [
  { key: 'shaft_flex', label: 'Shaft Flex', listField: 'shaft_flex_options' },
  { key: 'grip', label: 'Grips', listField: 'grip_options' },
  { key: 'grip_wraps', label: 'Grip Wraps', listField: 'grip_wraps_options' },
  { key: 'grip_logo', label: 'Grip Logo', listField: 'grip_logo_options' },
  { key: 'playing_length', label: 'Playing Length', listField: 'playing_length_options' },
  { key: 'tipping', label: 'Tipping', listField: 'tipping_options' },
  { key: 'pured', label: 'Pured', listField: 'pured_options' },
];

function PriceOverridesSection({ formData, setFormData }) {
  const overrides = formData.option_price_overrides || {};

  // Only show rows for categories where this product actually has selected options
  const activeCategories = OVERRIDE_CATEGORIES.filter(
    (c) => (formData[c.listField] || []).length > 0
  );

  if (activeCategories.length === 0) {
    return null;
  }

  const setOverride = (categoryKey, optionId, value) => {
    setFormData((prev) => {
      const next = { ...(prev.option_price_overrides || {}) };
      const cat = { ...(next[categoryKey] || {}) };
      if (value === '' || value === null || Number.isNaN(value)) {
        delete cat[optionId];
      } else {
        cat[optionId] = Number(value);
      }
      if (Object.keys(cat).length === 0) {
        delete next[categoryKey];
      } else {
        next[categoryKey] = cat;
      }
      return { ...prev, option_price_overrides: next };
    });
  };

  const getOverrideValue = (categoryKey, optionId) => {
    const v = overrides?.[categoryKey]?.[optionId];
    return v === undefined || v === null ? '' : String(v);
  };

  return (
    <div className="bg-white p-6 rounded-lg border border-[#E8E4DC]" data-testid="price-overrides-section">
      <div className="flex items-start justify-between mb-1">
        <h3 className="text-xl font-bold text-[#2C3E2D]">Price Overrides</h3>
      </div>
      <p className="text-[#5A6B5C] text-sm mb-4">
        Waive the upcharge on specific shafts, grips, or other options when customers buy this product.
        Leave blank to use the option&apos;s default price. Enter <span className="font-semibold">0</span> to
        mark it as <span className="font-semibold">Included</span> (no upcharge). Any other value replaces
        the default upcharge for this product only.
      </p>

      <div className="space-y-6">
        {activeCategories.map((cat) => {
          const selected = formData[cat.listField] || [];
          return (
            <div key={cat.key} data-testid={`override-group-${cat.key}`}>
              <h4 className="text-sm font-bold text-[#334155] uppercase tracking-wider mb-2">{cat.label}</h4>
              <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
                {selected.map((opt) => {
                  const defaultPrice = Number(opt.price_modifier || 0);
                  const ovVal = getOverrideValue(cat.key, opt.id);
                  const ovNum = ovVal === '' ? null : Number(ovVal);
                  return (
                    <div
                      key={opt.id}
                      className="flex items-center gap-3 px-3 py-2 bg-white"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{opt.name}</p>
                        <p className="text-xs text-gray-500">
                          Default: {defaultPrice > 0 ? `+$${defaultPrice}` : 'No upcharge'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">Override $</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={ovVal}
                          onChange={(e) => setOverride(cat.key, opt.id, e.target.value === '' ? '' : parseFloat(e.target.value))}
                          placeholder="default"
                          className="w-24 px-2 py-1 text-sm border border-gray-200 rounded focus:border-[#334155] focus:outline-none"
                          data-testid={`override-input-${cat.key}-${opt.id}`}
                        />
                        {ovNum === 0 && (
                          <span className="text-xs font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded">
                            Included
                          </span>
                        )}
                        {ovVal !== '' && (
                          <button
                            type="button"
                            onClick={() => setOverride(cat.key, opt.id, '')}
                            className="text-xs text-gray-400 hover:text-gray-600"
                            title="Clear override"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}



// Map club category → typical shaft subcategory in the master catalog. Used
// only to default the filter chips; admins can still see every shaft.
const CATEGORY_TO_SHAFT_SUB = {
  drivers: 'wood',
  fairways: 'wood',
  hybrids: 'hybrid',
  irons: 'iron',
  wedges: 'wedge',
};

const SHAFT_SUB_CHIPS = [
  { key: 'wood', label: 'Wood' },
  { key: 'hybrid', label: 'Hybrid' },
  { key: 'iron', label: 'Iron' },
  { key: 'wedge', label: 'Wedge' },
  { key: 'unspecified', label: 'Unspecified' }, // subcategory null
];

function CatalogShaftOverridesSection({ formData, setFormData }) {
  const [shafts, setShafts] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [copyOpen, setCopyOpen] = React.useState(false);
  const [applyAllBusy, setApplyAllBusy] = React.useState(false);
  const [categoryProducts, setCategoryProducts] = React.useState([]);
  // Type filter starts on the club-appropriate subcategory, but the admin
  // can flip to "All" (or any other type) to add overrides for shafts we
  // don't automatically show. `null` = all types (no filter).
  const defaultSub = CATEGORY_TO_SHAFT_SUB[formData.category] || null;
  const [typeFilter, setTypeFilter] = React.useState(defaultSub);

  const catalogOverrides = React.useMemo(
    () => (formData.option_price_overrides || {}).catalog_shaft || {},
    [formData.option_price_overrides]
  );

  // Reset the default filter when the club category changes.
  React.useEffect(() => {
    setTypeFilter(CATEGORY_TO_SHAFT_SUB[formData.category] || null);
  }, [formData.category]);

  React.useEffect(() => {
    let cancel = false;
    const load = async () => {
      setLoading(true);
      try {
        // Fetch EVERY shaft so admins can always add an override — even to
        // shafts whose subcategory doesn't match this club's type.
        const res = await api.get('/products', { params: { category: 'shafts' } });
        if (!cancel) setShafts(Array.isArray(res.data) ? res.data : (res.data.items || []));
      } catch (err) {
        if (!cancel) toast.error('Failed to load catalog shafts');
      } finally {
        if (!cancel) setLoading(false);
      }
    };
    load();
    return () => { cancel = true; };
  }, []);

  // Load sibling clubs (same category, this tenant) so the admin can copy
  // overrides from another club or apply the current set to all of them.
  React.useEffect(() => {
    let cancel = false;
    if (!formData.category || !['drivers', 'fairways', 'hybrids', 'irons', 'wedges'].includes(formData.category)) {
      setCategoryProducts([]);
      return () => {};
    }
    (async () => {
      try {
        const res = await api.get('/admin/products', { params: { category: formData.category } });
        const items = Array.isArray(res.data) ? res.data : (res.data.items || []);
        if (!cancel) setCategoryProducts(items);
      } catch (err) {
        // non-fatal — copy/apply-all buttons just won't render
      }
    })();
    return () => { cancel = true; };
  }, [formData.category]);

  const copyFromProduct = (sourceProduct) => {
    const src = (sourceProduct?.option_price_overrides || {}).catalog_shaft || {};
    const srcKeys = Object.keys(src);
    if (srcKeys.length === 0) {
      toast.info('That club has no catalog-shaft overrides to copy.');
      return;
    }
    setFormData((prev) => {
      const next = { ...(prev.option_price_overrides || {}) };
      const cat = { ...(next.catalog_shaft || {}) };
      let added = 0;
      for (const k of srcKeys) {
        if (cat[k] === undefined) added += 1;
        cat[k] = Number(src[k]);
      }
      next.catalog_shaft = cat;
      toast.success(`Copied ${srcKeys.length} overrides from ${sourceProduct.name} (${added} new). Click Save Product to persist.`);
      return { ...prev, option_price_overrides: next };
    });
    setCopyOpen(false);
  };

  const applyToAllInCategory = async () => {
    const overridesCount = Object.keys(catalogOverrides).length;
    if (overridesCount === 0) {
      toast.info('No overrides on this club to apply.');
      return;
    }
    const siblings = categoryProducts.filter((p) => p.product_id !== formData.product_id);
    if (siblings.length === 0) {
      toast.info('No other clubs in this category to apply to.');
      return;
    }
    const label = formData.category;
    const ok = window.confirm(
      `Apply these ${overridesCount} no-upcharge shaft overrides to ALL ${siblings.length} other ${label}? ` +
      `Existing overrides on those clubs will be MERGED with these (values you set here win). This cannot be undone.`
    );
    if (!ok) return;
    try {
      setApplyAllBusy(true);
      const res = await api.post('/admin/products/copy-catalog-shaft-overrides', {
        overrides: catalogOverrides,
        target_scope: 'category',
        target_category: formData.category,
        source_product_id: formData.product_id,
        mode: 'merge',
      });
      const count = res.data?.updated_count ?? 0;
      toast.success(`Applied to ${count} ${label}.`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Failed to apply overrides';
      toast.error(msg);
    } finally {
      setApplyAllBusy(false);
    }
  };

  const setOverride = (shaftProductId, value) => {
    setFormData((prev) => {
      const next = { ...(prev.option_price_overrides || {}) };
      const cat = { ...(next.catalog_shaft || {}) };
      if (value === '' || value === null || Number.isNaN(value)) {
        delete cat[shaftProductId];
      } else {
        cat[shaftProductId] = Number(value);
      }
      if (Object.keys(cat).length === 0) {
        delete next.catalog_shaft;
      } else {
        next.catalog_shaft = cat;
      }
      return { ...prev, option_price_overrides: next };
    });
  };

  const q = search.trim().toLowerCase();
  const overrideIds = new Set(Object.keys(catalogOverrides));

  // Counts per type chip (respect the current search but not the type filter,
  // so the chip labels always show the pool the admin is choosing from).
  const searchFiltered = shafts.filter((s) => {
    if (!q) return true;
    const hay = `${s.name || ''} ${s.manufacturer || ''}`.toLowerCase();
    return hay.includes(q);
  });
  const typeCount = (key) =>
    searchFiltered.filter((s) => {
      if (key === 'unspecified') return !s.subcategory;
      return s.subcategory === key;
    }).length;
  const totalCount = searchFiltered.length;

  const filtered = searchFiltered.filter((s) => {
    if (!typeFilter) return true;
    if (typeFilter === 'unspecified') return !s.subcategory;
    return s.subcategory === typeFilter;
  });

  // Always float overridden shafts (in the current filter) to the top.
  const sorted = [...filtered].sort((a, b) => {
    const ao = overrideIds.has(a.product_id) ? 0 : 1;
    const bo = overrideIds.has(b.product_id) ? 0 : 1;
    if (ao !== bo) return ao - bo;
    return (a.name || '').localeCompare(b.name || '');
  });

  return (
    <div className="bg-white p-6 rounded-lg border border-[#E8E4DC]" data-testid="catalog-shaft-overrides-section">
      <div className="flex items-start justify-between mb-1">
        <h3 className="text-xl font-bold text-[#2C3E2D]">Catalog Shaft Overrides</h3>
        <span className="text-xs text-gray-500">{overrideIds.size} override{overrideIds.size === 1 ? '' : 's'} set</span>
      </div>
      <p className="text-[#5A6B5C] text-sm mb-3">
        Waive or replace the price of specific shafts from the master shaft catalog when a customer
        pairs them with this club. Enter <span className="font-semibold">0</span> for
        <span className="font-semibold"> No Upcharge</span>, or any other value to replace the shaft&apos;s
        catalog price. Blank uses the shaft&apos;s normal catalog price. Every shaft in the catalog is
        listed here — switch <span className="font-semibold">Type</span> to <span className="font-semibold">All</span>
        to add overrides to shafts that aren&apos;t normally used with this club.
      </p>

      {['drivers', 'fairways', 'hybrids', 'irons', 'wedges'].includes(formData.category) && (
        <div className="flex flex-wrap items-center gap-2 mb-4 pb-4 border-b border-gray-100">
          <button
            type="button"
            onClick={() => setCopyOpen(true)}
            disabled={categoryProducts.length <= 1}
            className="text-xs font-semibold px-3 py-1.5 rounded border border-[#334155] text-[#334155] hover:bg-[#334155] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid="copy-from-another-club-btn"
          >
            Copy from another club
          </button>
          <button
            type="button"
            onClick={applyToAllInCategory}
            disabled={applyAllBusy || overrideIds.size === 0 || categoryProducts.length <= 1}
            className="text-xs font-semibold px-3 py-1.5 rounded bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid="apply-to-all-btn"
          >
            {applyAllBusy
              ? 'Applying...'
              : `Apply to all ${Math.max(0, categoryProducts.length - 1)} other ${formData.category}`}
          </button>
        </div>
      )}

      {copyOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setCopyOpen(false)}
          data-testid="copy-from-modal"
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <h4 className="text-lg font-bold text-[#2C3E2D]">Copy overrides from another {formData.category?.replace(/s$/, '')}</h4>
              <button
                type="button"
                onClick={() => setCopyOpen(false)}
                className="text-gray-400 hover:text-gray-700"
                data-testid="copy-from-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-3 border-b border-gray-100 text-xs text-gray-500">
              Overrides will be MERGED into the current form. Save the product to persist.
            </div>
            <div className="overflow-y-auto divide-y divide-gray-100">
              {categoryProducts
                .filter((p) => p.product_id !== formData.product_id)
                .map((p) => {
                  const cnt = Object.keys((p.option_price_overrides || {}).catalog_shaft || {}).length;
                  return (
                    <button
                      key={p.product_id}
                      type="button"
                      onClick={() => copyFromProduct(p)}
                      className="w-full text-left flex items-center gap-3 px-5 py-2.5 hover:bg-gray-50"
                      data-testid={`copy-from-source-${p.product_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
                        <p className="text-xs text-gray-500 truncate">
                          {p.manufacturer || 'Unknown brand'}
                        </p>
                      </div>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                        cnt > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-400'
                      }`}>
                        {cnt} override{cnt === 1 ? '' : 's'}
                      </span>
                    </button>
                  );
                })}
              {categoryProducts.filter((p) => p.product_id !== formData.product_id).length === 0 && (
                <p className="p-6 text-center text-sm text-gray-500">No other clubs in this category.</p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mb-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search shafts by name or brand..."
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:border-[#334155] focus:outline-none"
          data-testid="catalog-shaft-search"
        />
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid="catalog-shaft-type-chips">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider mr-1">Type:</span>
        <button
          type="button"
          onClick={() => setTypeFilter(null)}
          className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
            typeFilter === null
              ? 'bg-[#334155] text-white border-[#334155]'
              : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
          }`}
          data-testid="catalog-shaft-type-all"
        >
          All ({totalCount})
        </button>
        {SHAFT_SUB_CHIPS.map((chip) => {
          const count = typeCount(chip.key);
          return (
            <button
              key={chip.key}
              type="button"
              onClick={() => setTypeFilter(chip.key)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                typeFilter === chip.key
                  ? 'bg-[#334155] text-white border-[#334155]'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
              }`}
              data-testid={`catalog-shaft-type-${chip.key}`}
            >
              {chip.label} ({count})
            </button>
          );
        })}
      </div>

      {loading ? (
        <p className="text-sm text-gray-500 py-6 text-center">Loading catalog shafts...</p>
      ) : sorted.length === 0 ? (
        <p className="text-sm text-gray-500 py-6 text-center">
          No shafts match. Try clearing the search or picking a different type.
        </p>
      ) : (
        <>
          <p className="text-xs text-gray-500 mb-2">
            Showing {sorted.length} shaft{sorted.length === 1 ? '' : 's'}. Overrides are pinned to the top. Scroll to see the rest.
          </p>
          <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden max-h-[560px] overflow-y-auto">
            {sorted.map((s) => {
              const ovVal = catalogOverrides[s.product_id];
              const ovStr = ovVal === undefined || ovVal === null ? '' : String(ovVal);
              const ovNum = ovStr === '' ? null : Number(ovStr);
              const catalogPrice = Number(s.min_advertised_price || 0);
              return (
                <div
                  key={s.product_id}
                  className="flex items-center gap-3 px-3 py-2 bg-white"
                  data-testid={`catalog-shaft-row-${s.product_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{s.name}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {s.manufacturer ? `${s.manufacturer} • ` : ''}
                      {s.subcategory ? `${s.subcategory} • ` : ''}
                      Catalog: ${catalogPrice.toFixed(2)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Override $</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={ovStr}
                      onChange={(e) =>
                        setOverride(
                          s.product_id,
                          e.target.value === '' ? '' : parseFloat(e.target.value)
                        )
                      }
                      placeholder="default"
                      className="w-24 px-2 py-1 text-sm border border-gray-200 rounded focus:border-[#334155] focus:outline-none"
                      data-testid={`catalog-shaft-input-${s.product_id}`}
                    />
                    {ovNum === 0 ? (
                      <button
                        type="button"
                        onClick={() => setOverride(s.product_id, '')}
                        className="text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-2 py-0.5 rounded"
                        title="Click to clear this override"
                        data-testid={`catalog-shaft-clear-${s.product_id}`}
                      >
                        No Upcharge
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setOverride(s.product_id, 0)}
                        className="text-xs font-medium text-gray-600 border border-gray-200 hover:border-emerald-500 hover:text-emerald-700 px-2 py-0.5 rounded"
                        title="Set this shaft as No Upcharge on this club"
                        data-testid={`catalog-shaft-set-free-${s.product_id}`}
                      >
                        Set free
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
